import { httpClient } from '@/api/httpClient';
import {
  mapPaginatedResource,
  type PaginatedApiResource,
  type PaginatedResult,
} from '@/api/pagination.types';
import {
  mapApiResourceToAcceptedInvitation,
  mapApiResourceToAcceptedMembership,
  mapApiResourceToInvitation,
  mapApiResourceToInvitationPreview,
  mapApiResourceToInvitationWithLink,
  type AcceptedInvitation,
  type AcceptedInvitationApiResource,
  type AcceptedMembership,
  type AcceptedMembershipApiResource,
  type AcceptInvitationPayload,
  type CreateInvitationPayload,
  type Invitation,
  type InvitationApiResource,
  type InvitationPreview,
  type InvitationPreviewApiResource,
  type InvitationWithLink,
  type InvitationWithLinkApiResource,
} from './invitationsApi.types';

export async function createInvitation(
  payload: CreateInvitationPayload
): Promise<InvitationWithLink> {
  const response = await httpClient.post<InvitationWithLinkApiResource>('/invitations', {
    email: payload.email,
    full_name: payload.fullName,
    role_id: payload.roleId,
    scope_code: payload.scopeCode,
  });
  return mapApiResourceToInvitationWithLink(response.data);
}

export async function resendInvitation(invitationId: string): Promise<InvitationWithLink> {
  const response = await httpClient.post<InvitationWithLinkApiResource>(
    '/invitations/' + invitationId + '/resend'
  );
  return mapApiResourceToInvitationWithLink(response.data);
}

export async function generateInvitationLink(invitationId: string): Promise<InvitationWithLink> {
  const response = await httpClient.post<InvitationWithLinkApiResource>(
    '/invitations/' + invitationId + '/link'
  );
  return mapApiResourceToInvitationWithLink(response.data);
}

export async function revokeInvitation(invitationId: string): Promise<Invitation> {
  const response = await httpClient.post<InvitationApiResource>(
    '/invitations/' + invitationId + '/revoke'
  );
  return mapApiResourceToInvitation(response.data);
}

export async function fetchInvitations(
  page: number,
  pageSize: number,
  scopeCode?: string,
  status?: string
): Promise<PaginatedResult<Invitation>> {
  const params: Record<string, string | number> = { page, page_size: pageSize };
  if (scopeCode !== undefined) {
    params.scope_code = scopeCode;
  }
  if (status !== undefined) {
    params.status = status;
  }
  const response = await httpClient.get<PaginatedApiResource<InvitationApiResource>>(
    '/invitations',
    { params }
  );
  return mapPaginatedResource(response.data, mapApiResourceToInvitation);
}

export async function previewInvitation(token: string): Promise<InvitationPreview> {
  const response = await httpClient.get<InvitationPreviewApiResource>('/invitations/preview', {
    params: { token },
  });
  return mapApiResourceToInvitationPreview(response.data);
}

export async function acceptInvitation(
  payload: AcceptInvitationPayload
): Promise<AcceptedInvitation> {
  const response = await httpClient.post<AcceptedInvitationApiResource>('/invitations/accept', {
    token: payload.token,
    full_name: payload.fullName,
    phone: payload.phone,
    password: payload.password,
    preferred_language: payload.preferredLanguage,
    accept_terms: payload.acceptTerms,
  });
  return mapApiResourceToAcceptedInvitation(response.data);
}

export async function acceptInvitationAsExistingUser(
  token: string,
  acceptTerms: boolean
): Promise<AcceptedMembership> {
  const response = await httpClient.post<AcceptedMembershipApiResource>(
    '/invitations/accept-existing',
    { token, accept_terms: acceptTerms }
  );
  return mapApiResourceToAcceptedMembership(response.data);
}
